import { CheckCircleIcon } from '@phosphor-icons/react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useConfig } from '@entities/config/api';
import type { AuthSessionResponse } from '@entities/user/api';

import { useLegalDocuments, useRequestAuthCode, useVerifyAuthCode } from '../api';

import styles from './AuthFlow.module.css';
import { EmailStep } from './EmailStep';
import { OtpStep, type OtpChallenge } from './OtpStep';

interface AuthFlowProps {
  /** id заголовка: диалог связывает его с собой через aria-labelledby. */
  titleId: string;
  /**
   * Уровень заголовка. В модальном окне это `h2` (заголовок страницы под ним
   * остаётся первым), на отдельной странице `/login` — `h1`: экран без `h1`
   * ломает иерархию заголовков.
   */
  headingLevel?: 'h1' | 'h2' | undefined;
  onSuccess: (session: AuthSessionResponse) => void;
  /** Начальный адрес — например, из профиля при повторном входе. */
  initialEmail?: string | undefined;
}

const FALLBACK_OTP = {
  code_length: 6,
  ttl_seconds: 600,
  resend_after_seconds: 30,
};

/**
 * Двухшаговый вход: адрес (FE-020) → код из письма (FE-021).
 *
 * Состояние challenge живёт только в памяти компонента и намеренно не попадает
 * в `sessionStorage`: перезагрузка страницы возвращает на первый шаг, зато
 * адрес пользователя не остаётся в хранилище вкладки (MVP_TECH_RFC §22).
 */
export function AuthFlow({
  titleId,
  headingLevel = 'h2',
  onSuccess,
  initialEmail = '',
}: AuthFlowProps) {
  // Тег заголовка выбирает вызывающий экран: см. `headingLevel`.
  const Heading = headingLevel;

  const { t, i18n } = useTranslation();
  const { data: config } = useConfig();
  const legalQuery = useLegalDocuments();

  const [email, setEmail] = useState(initialEmail);
  const [challenge, setChallenge] = useState<OtpChallenge | null>(null);

  const requestCode = useRequestAuthCode();
  const verifyCode = useVerifyAuthCode();

  const otp = config?.auth
    ? {
        code_length: config.auth.otp_code_length,
        ttl_seconds: config.auth.otp_ttl_seconds,
        resend_after_seconds: config.auth.otp_resend_after_seconds,
      }
    : FALLBACK_OTP;

  // Язык письма — язык интерфейса, в котором пользователь заполняет форму.
  // `resolvedLanguage` всегда из supportedLngs, поэтому backend его примет.
  const locale = i18n.resolvedLanguage ?? config?.default_locale ?? 'de';

  const startChallenge = useCallback(
    (targetEmail: string, legalDocumentIds: string[]) => {
      requestCode.mutate(
        { email: targetEmail, locale, legalDocumentIds },
        {
          onSuccess: (issued) => {
            const now = Date.now();
            setChallenge({
              challengeId: issued.challenge_id,
              email: targetEmail,
              expiresAt: new Date(now + issued.expires_in_seconds * 1000),
              resendAt: new Date(now + issued.resend_available_in_seconds * 1000),
            });
          },
        },
      );
    },
    [locale, requestCode],
  );

  // Успех — единственный выход из потока: дальше решает вызывающий экран.
  const session = verifyCode.data;
  useEffect(() => {
    if (session) onSuccess(session);
  }, [session, onSuccess]);

  if (session) {
    return (
      <div className={styles.done} role="status">
        <CheckCircleIcon size={40} weight="fill" aria-hidden="true" className={styles.doneIcon} />
        <Heading id={titleId} className={styles.title}>
          {t('auth.success.title')}
        </Heading>
        <p className={styles.lead}>{t('auth.success.lead')}</p>
      </div>
    );
  }

  if (!challenge) {
    return (
      <EmailStep
        titleId={titleId}
        headingLevel={headingLevel}
        email={email}
        onEmailChange={setEmail}
        legalDocuments={legalQuery.data ?? []}
        legalPending={legalQuery.isPending}
        submitting={requestCode.isPending}
        error={requestCode.error}
        onSubmit={({ email: value, legalDocumentIds }) => {
          setEmail(value);
          startChallenge(value, legalDocumentIds);
        }}
      />
    );
  }

  return (
    <OtpStep
      titleId={titleId}
      headingLevel={headingLevel}
      challenge={challenge}
      codeLength={otp.code_length}
      submitting={verifyCode.isPending}
      resending={requestCode.isPending}
      verifyError={verifyCode.error}
      resendError={requestCode.error}
      onVerify={(code) => {
        verifyCode.mutate({ challengeId: challenge.challengeId, code });
      }}
      onResend={() => {
        // Новый код аннулирует предыдущий на стороне backend (§7.1), поэтому
        // прежние ошибки проверки больше не относятся к делу.
        verifyCode.reset();
        startChallenge(challenge.email, legalQuery.data?.map((document) => document.id) ?? []);
      }}
      onChangeEmail={() => {
        verifyCode.reset();
        requestCode.reset();
        setChallenge(null);
      }}
    />
  );
}
